import {
  Box,
  Button,
  Heading,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDocumentByUUID } from "../../../../api/documentsService";
import { updateStatusForTimeline } from "../../../../api/timelineService";
import useCustomToast from "../../../../customHooks/useCustomToast";
import { setDocumentsFromApiCall } from "../../../../Redux/documentsSlice";
import {
  cancelUpload,
  initiateCancelUploadProcess,
  keepUploading,
  retryUpload,
} from "../../../../utils/cancelFileUpload";
import { uploadFileWithProgress } from "../../../../utils/UTILITY_METHODS";
import FileUpload from "../../../FileUpload";
import CancelFileUploadModal from "../../../Modals/CancelFileUploadModal";
import FillBankDetailsModal from "../../../Modals/FillBankDetailsModal";
import UploadProgress from "../../../UploadProgress";
import SingleBankStatement from "./SingleBankStatement";

const BankStatements = ({ handleUserTabClicked }) => {
  const { bankStatements } = useSelector((store) => store.documentsDetails);
  const [bankUploadSuccess, setBankUploadSuccess] = useState(false);
  const [bankUploadProgress, setBankUploadProgress] = useState(0);
  const [isBankUploading, setIsBankUploading] = useState(false);
  const [bankUploadFailed, setBankUploadFailed] = useState(false);
  const [bankDetailFile, setBankDetailFile] = useState(null);
  const [lastBankFile, setLastBankFile] = useState(null);
  const [formValues, setFormValues] = useState({
    bankName: "",
    accountNumber: "",
    accountType: "",
  });
  const dispatch = useDispatch();
  const showToast = useCustomToast();

  // Create a ref to store the AbortController
  const abortControllerRef = useRef(null);
  const inputRef = useRef(null);

  // Function to trigger the hidden input click
  const handleInputClick = () => {
    inputRef.current.click(); // This triggers the hidden input element
  };

  // State for the  confirmation modal
  // State for the  cancel file upload confirmation modal
  const {
    isOpen: isCancelFileUploadModalOpen,
    onOpen: openCancelFileUploadModal,
    onClose: closeCancelFileUploadModal,
  } = useDisclosure();

  // State for the  bank details modal
  const {
    isOpen: isBankDetailsModalOpen,
    onOpen: openBankDetailsModal,
    onClose: closeBankDetailsModal,
  } = useDisclosure();

  // Bank Document Upload
  const handleBankUpload = async (files) => {
    const file = files[0];
    setLastBankFile(file); // Store file for retry
    setBankUploadFailed(false); // Reset failure state
    setIsBankUploading(true); // Show uploading state

    // Initialize a new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const data = await uploadFileWithProgress(
        file,
        setBankUploadProgress,
        setBankUploadFailed,
        setBankUploadSuccess,
        {
          documentType: "bank",
          documentSubType: "bank_statement",
          document_type_code: formValues.bankName,
          documentNumber: formValues.accountNumber, // bank account number from modal
          documentNumberIdentifier: "bank_account_number",
          level: "user",
        },
        abortController.signal // Pass the signal
      );
      setIsBankUploading(false);
      setBankUploadProgress(0);
      setFormValues({
        bankName: "",
        accountNumber: "",
        accountType: "",
      });
      showToast(data.message, "success");
      if (isCancelFileUploadModalOpen) closeCancelFileUploadModal();
      const bankDocumentUUID = data.data;
      const DocumentDetails = await getDocumentByUUID(bankDocumentUUID);
      dispatch(setDocumentsFromApiCall([DocumentDetails.data]));
      abortControllerRef.current = null; // Reset the controller
    } catch (error) {
      if (error.message === "Upload canceled") {
        showToast("Upload canceled", "warning");
      } else {
        showToast(error.message || "An error occurred during upload", "error");
      }
      setIsBankUploading(false);
      abortControllerRef.current = null; // Reset the controller
    }
  };

  // Handle form submission (Bank Details Modal)
  const handleFormSubmit = () => {
    if (
      formValues.bankName &&
      formValues.accountNumber &&
      formValues.accountType
    ) {
      closeBankDetailsModal(); // Close the modal after successful submission
      if (bankDetailFile) {
        const file = bankDetailFile;
        handleBankUpload(file);
        setBankDetailFile(null);
      } else {
        handleInputClick();
      }
    } else {
      showToast("All fields are required.", "warning");
    }
  };

  // Reset modal and form state on cancel
  const handleBankDetailsModalCancel = () => {
    setFormValues({ bankName: "", accountNumber: "", accountType: "" });
    setBankUploadProgress(0);
    closeBankDetailsModal();
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      minHeight="100px" // Ensure there's a minimum height to avoid content squishing
      maxHeight="auto" // Allow the component to grow as necessary
    >
      <Box>
        <Heading as="h2" size="md">
          Bank Statements
        </Heading>
        <Text color={"gray.600"} fontSize="sm" mt={2}>
          Upload your bank account statements for the past 6 months. Ensure that
          the statements are clear and include account details, transaction
          history, and balances.
        </Text>
        <Stack
          direction={{ base: "column", md: "row" }}
          spacing={2}
          mt={5}
          onClick={() => {
            openBankDetailsModal();
          }}
        >
          <FileUpload
            onFileUpload={handleBankUpload}
            width={"100%"}
            isUploading={isBankUploading}
            uploadFailed={bankUploadFailed}
            uploadSuccess={bankUploadSuccess}
            isIdentificationDocument={false}
            inputRef={inputRef}
            setFillDetailsModalOpen={openBankDetailsModal}
            setFileToUpload={setBankDetailFile}
            acceptImageOrPdf={false}
          />
        </Stack>

        <FillBankDetailsModal
          isBankDetailsModalOpen={isBankDetailsModalOpen}
          closeBankDetailsModal={closeBankDetailsModal}
          setFormValues={setFormValues}
          formValues={formValues}
          handleFormSubmit={handleFormSubmit}
          handleBankDetailsModalCancel={handleBankDetailsModalCancel}
        />

        {/* Bank Document Upload Progress */}
        {isBankUploading && (
          <UploadProgress
            documentType="Bank Document"
            isUploading={isBankUploading}
            uploadProgress={bankUploadProgress}
            uploadFailed={bankUploadFailed}
            initiateCancelUploadProcess={() =>
              initiateCancelUploadProcess(
                abortControllerRef,
                openCancelFileUploadModal
              )
            }
            onRetry={() =>
              retryUpload(bankUploadFailed, lastBankFile, handleBankUpload)
            }
          />
        )}
      </Box>

      {/* Cancel Upload Confirmation Modal */}
      <CancelFileUploadModal
        isModalOpen={isCancelFileUploadModalOpen}
        closeModal={closeCancelFileUploadModal}
        cancelUpload={() =>
          cancelUpload(
            abortControllerRef,
            setIsBankUploading,
            setBankUploadProgress,
            closeCancelFileUploadModal
          )
        }
        keepUploading={() => keepUploading(closeCancelFileUploadModal)}
      />

      <Stack spacing={3} mt={5}>
        {bankStatements.map((document, index) => (
          <SingleBankStatement
            key={document.uuid || index}
            name={
              document?.original_filename.length > 20
                ? `${document?.original_filename.substring(0, 20)}...`
                : document?.original_filename
            }
            isLast={index === bankStatements.length - 1}
            scannedCopyDetected={index === 2}
            detectedMessage={"Scanned Copy Detected"}
          />
        ))}
      </Stack>

      <Box display={"flex"} justifyContent={"flex-end"} columnGap={4} mt={5}>
        <Button
          colorScheme="blackAlpha"
          color={"blackAlpha.700"}
          size="sm"
          variant="outline"
          px={12}
          onClick={() => handleUserTabClicked(0)}
        >
          Back
        </Button>
        <Button
          colorScheme={!Boolean(bankStatements.length) ? "blackAlpha" : "teal"}
          color={!Boolean(bankStatements.length) ? "blackAlpha" : "white"}
          size="sm"
          variant="solid"
          px={12}
          disabled={!Boolean(bankStatements.length)}
          onClick={() => {
            handleUserTabClicked(2);
            //update user timeline on server
            updateStatusForTimeline(
              5,
              "financial_information",
              "bank_statements",
              "completed"
            );
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default BankStatements;
